import { useCallback, useState } from "react";

import {
    addIikoTerminal,
    removeIikoTerminal,
    syncIikoNomenclature,
    updateIikoApiLogin,
    updateIikoIntegrationEnabled,
    connectIikoWebhook,
    updateIikoCatalogSource,
    updateIikoPriceSyncEnabled,
    updateIikoOrganization,
} from "services/menu/rest";
import {
    IikoCatalogSourceType,
    IikoIntegrationDto,
    IikoWebhookDto,
} from "services/menu/restModels";
import { ConnectIikoWebhookRequestDto } from "services/menu/restRequests";

import { UseNotificationSnackbarReturn } from "shared/ui";

import { IikoIntegrationSaving, IikoIntegrationSavingKind } from "features/IikoIntegration/types";

export interface IikoIntegrationMutations {
    saving: IikoIntegrationSaving;
    saveApiLogin: (apiLogin: string) => Promise<IikoIntegrationDto | null>;
    saveOrganization: (iikoOrganizationId: string) => Promise<IikoIntegrationDto | null>;
    saveEnabled: (isEnabled: boolean) => Promise<IikoIntegrationDto | null>;
    saveCatalogSource: (
        source: IikoCatalogSourceType,
        externalMenuId: string | null,
        priceCategoryId: string | null
    ) => Promise<IikoIntegrationDto | null>;
    savePriceSyncEnabled: (isEnabled: boolean) => Promise<IikoIntegrationDto | null>;
    connectWebhook: (settings: ConnectIikoWebhookRequestDto) => Promise<IikoWebhookDto | null>;
    addTerminal: (terminalGroupId: string) => Promise<IikoIntegrationDto | null>;
    removeTerminal: (terminalGroupId: string) => Promise<IikoIntegrationDto | null>;
    sync: () => Promise<IikoIntegrationDto | null>;
}

const NO_SAVING: IikoIntegrationSaving = {
    apiLogin: false,
    organization: false,
    enabled: false,
    catalogSource: false,
    webhook: false,
    priceSync: false,
    terminal: false,
    sync: false,
};

/*
* Мутации подключения одной обёрткой: флаг saving, тост об успехе и разбор ошибки живут здесь.
* Каждая ручка возвращает состояние интеграции целиком, поэтому вызывающему остаётся только
* положить ответ в стейт — перезапрашивать интеграцию после действия не нужно.
* Флаги раздельные: пока летит сохранение организации, кнопка синхронизации блокироваться не должна.
* */
export const useIikoIntegrationMutations = (
    menuId: number,
    notify: UseNotificationSnackbarReturn
): IikoIntegrationMutations => {
    const [saving, setSaving] = useState<IikoIntegrationSaving>(NO_SAVING);

    const run = useCallback(
        async <T>(
            kind: IikoIntegrationSavingKind,
            action: () => Promise<T | null>,
            successMessage: string,
            errorMessage: string
        ): Promise<T | null> => {
            setSaving((current) => ({ ...current, [kind]: true }));

            try {
                const result = await action();

                notify.showSuccess(successMessage);

                return result;
            } catch (error) {
                notify.showError(errorMessage, error);

                return null;
            } finally {
                setSaving((current) => ({ ...current, [kind]: false }));
            }
        },
        [notify]
    );

    const saveApiLogin = useCallback(
        (apiLogin: string) =>
            run(
                "apiLogin",
                () => updateIikoApiLogin(menuId, { apiLogin }),
                "Ключ Iiko Transport сохранён",
                "Не удалось сохранить ключ"
            ),
        [menuId, run]
    );

    const saveOrganization = useCallback(
        (iikoOrganizationId: string) =>
            run(
                "organization",
                () => updateIikoOrganization(menuId, { iikoOrganizationId }),
                "Организация сохранена",
                "Не удалось сохранить организацию"
            ),
        [menuId, run]
    );

    const saveEnabled = useCallback(
        (isEnabled: boolean) =>
            run(
                "enabled",
                () => updateIikoIntegrationEnabled(menuId, { isEnabled }),
                isEnabled ? "Интеграция включена" : "Интеграция выключена",
                "Не удалось переключить интеграцию"
            ),
        [menuId, run]
    );

    const saveCatalogSource = useCallback(
        (
            source: IikoCatalogSourceType,
            externalMenuId: string | null,
            priceCategoryId: string | null
        ) =>
            run(
                "catalogSource",
                () => updateIikoCatalogSource(menuId, { source, externalMenuId, priceCategoryId }),
                "Источник меню сохранён",
                "Не удалось сохранить источник меню"
            ),
        [menuId, run]
    );

    const savePriceSyncEnabled = useCallback(
        (isEnabled: boolean) =>
            run(
                "priceSync",
                () => updateIikoPriceSyncEnabled(menuId, { isEnabled }),
                isEnabled ? "Цены будут обновляться из iiko" : "Автообновление цен выключено",
                "Не удалось переключить обновление цен"
            ),
        [menuId, run]
    );

    const connectWebhook = useCallback(
        (settings: ConnectIikoWebhookRequestDto) =>
            run(
                "webhook",
                () => connectIikoWebhook(menuId, settings),
                "Уведомления из iiko подключены",
                "Не удалось подключить уведомления"
            ),
        [menuId, run]
    );

    const addTerminal = useCallback(
        (terminalGroupId: string) =>
            run(
                "terminal",
                () => addIikoTerminal(menuId, { terminalGroupId }),
                "Терминал добавлен",
                "Не удалось добавить терминал"
            ),
        [menuId, run]
    );

    const removeTerminal = useCallback(
        (terminalGroupId: string) =>
            run(
                "terminal",
                () => removeIikoTerminal(menuId, terminalGroupId),
                "Терминал удалён",
                "Не удалось удалить терминал"
            ),
        [menuId, run]
    );

    const sync = useCallback(
        () =>
            run(
                "sync",
                () => syncIikoNomenclature(menuId),
                "Номенклатура синхронизирована",
                "Не удалось синхронизировать номенклатуру"
            ),
        [menuId, run]
    );

    return {
        saving,
        saveApiLogin,
        saveOrganization,
        saveEnabled,
        saveCatalogSource,
        savePriceSyncEnabled,
        connectWebhook,
        addTerminal,
        removeTerminal,
        sync,
    };
};
